import { useUser } from "@p0security/frontend-core";
import { toRequestAttribution } from "@p0security/shared/permission-requests/attribution";
import { PermissionRequest } from "@p0security/shared/types/permission";
import { RequestStatuses } from "@p0security/shared/types/request-status";
import {
  Button,
  Empty,
  Flex,
  Grid,
  Space,
  Spin,
  Typography,
} from "@p0security/ui";
import type {
  ColumnTitle,
  FilterValue,
  TablePaginationConfig,
} from "@p0security/ui";
import { isKeyOf } from "@p0security/util/types/is";
import { useNavigateWithEnv } from "components/Assessment/hooks/useNavigateWithEnv";
import { Prefix } from "components/GraphTable/Prefix";
import { AllIntegrationLogos } from "components/Integrations/IntegrationLogo";
import {
  ResizableTable,
  ResizableTableColumnType,
  ResizableTableKeys,
} from "components/ResizableTable";
import {
  FormattedDate,
  FormattedTime,
} from "components/common/FormattedDateTime";
import { formatDistanceStrict, parseISO, startOfDay, subDays } from "date-fns";
import { usePagination } from "hooks/usePagination";
import { useFlags } from "launchdarkly-react-client-sdk";
import { compact, gt, lt, startCase, throttle, toLower, uniq } from "lodash";
import { RangeValueType } from "rc-picker/lib/PickerInput/RangePicker";
import React, { useCallback, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { spacing } from "styles/spacing";

import { ApproverCell } from "../components/ApproverCell";
import { NotifierConversationLinks } from "../components/ConversationLink";
import { FilterTag } from "../components/FilterTag";
import { RequestDetailDrawer } from "../components/RequestDetailsDrawer";
import { RequestDateFilters } from "../components/RequestHistoryDateFilters";
import { SearchBar } from "../components/RequestHistorySearchBar";
import { RequestStatusTag } from "../components/RequestStatusTag";
import { RequestorCell } from "../components/RequestorCell";
import { useRequests } from "../hooks/useRequests";
import { useURLParamState } from "../hooks/useURLParamState";
import {
  deserializeQueryParamValue,
  getPreApprovalData,
  requestorAttribution,
  serializeQueryParamValue,
  statusToDisplayText,
  toRequestAccessType,
  toRequestDescription,
  toRequestLocation,
  toRequestType,
} from "../requestUtils";
import { TimeRange } from "../types";

import "./RequestHistory.less";

type RequestRow = PermissionRequest & { id: string };

type RequestColumnName = RequestColumn["columnName"];
type TableFilters = Partial<Record<RequestColumnName, FilterValue | null>>;
type RequestColumn = Pick<ResizableTableColumnType<RequestRow>, "width"> & {
  columnName: string;
  sortable: boolean;
  filterable: boolean;
  renderFunc: (
    val: string,
    data: PermissionRequest,
    displayName: string
  ) => JSX.Element | undefined;
  toText: (r: PermissionRequest) => string | undefined;
  /** Specify additional text to consider when searching */
  toSearch?: (r: PermissionRequest) => string[];
};

const getTime = (
  search: URLSearchParams,
  param: string,
  defaultValue?: Date
): Date | null => {
  const raw = search.get(param);
  if (!raw) return defaultValue ?? null;
  const value = parseISO(raw);
  if (isNaN(value.getTime())) return null;
  return value;
};

const setTime = (search: URLSearchParams, param: string, time: Date | null) => {
  if (time === null) {
    search.delete(param);
  } else {
    search.set(param, time.toISOString());
  }
};

const useTimeRangeQuery = (
  [startParam, endParam]: [string, string],
  options?: { startDefault?: Date; endDefault?: Date }
): [TimeRange, (value: RangeValueType<Date> | null | undefined) => void] => {
  const [search, setSearch] = useSearchParams();

  const range = useMemo<TimeRange>(
    () => [
      getTime(search, startParam, options?.startDefault),
      getTime(search, endParam, options?.endDefault),
    ],
    [endParam, options, search, startParam]
  );
  const setRange = useCallback(
    (input: RangeValueType<Date> | null | undefined) => {
      const next = new URLSearchParams(search);
      setTime(next, startParam, input?.[0] ?? null);
      setTime(next, endParam, input?.[1] ?? null);
      setSearch(next);
    },
    [endParam, search, setSearch, startParam]
  );
  return [range, setRange];
};

const quickFilterAction = (
  columnName: string,
  filter: string,
  tableFilters: TableFilters,
  setTableFilters: (filters: TableFilters) => void
) => {
  const newFilters = {
    ...tableFilters,
    [columnName]: tableFilters[columnName]?.includes(filter)
      ? tableFilters[columnName]
      : (tableFilters[columnName] ?? [])?.concat([filter]),
  };
  setTableFilters(newFilters);
};

export const RequestHistory: React.FC<object> = () => {
  const { requestId } = useParams();
  const flags = useFlags();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigateWithEnv();
  const location = useLocation();

  const { user } = useUser();
  const userEmail = user?.email;

  const { md } = Grid.useBreakpoint();

  const startDefault = useMemo(() => startOfDay(subDays(new Date(), 30)), []);

  const showRequestInfo = useCallback(
    (id: string) => {
      navigate({ pathname: id, search: location.search });
    },
    [navigate, location.search]
  );

  const [requestedFilter, setRequestedFilter] = useTimeRangeQuery(
    ["requestedStart", "requestedEnd"],
    { startDefault }
  );

  const [approvedFilter, setApprovedFilter] = useTimeRangeQuery([
    "approvedStart",
    "approvedEnd",
  ]);

  const { hasRequests, loading, requests } = useRequests(RequestStatuses, {
    since: requestedFilter[0]?.getTime(),
  });
  const data = useMemo(
    () => requests.map((doc) => ({ id: doc.id, ...doc.data })),
    [requests]
  );

  const handleRowClick = useCallback(
    (record: RequestRow) => ({
      tabIndex: 0,
      onClick: () => showRequestInfo(record.id),
      onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          showRequestInfo(record.id);
        }
      },
    }),
    [showRequestInfo]
  );

  const [searchQuery, setSearchQuery] = useURLParamState("q");

  const requestColumns: RequestColumn[] = useMemo(
    () => [
      {
        columnName: "status",
        sortable: true,
        filterable: true,
        toText: (r) => statusToDisplayText(r.status),
        renderFunc: (_val, request) => <RequestStatus request={request} />,
        width: 125,
      },
      {
        columnName: "requested",
        sortable: true,
        filterable: false,
        toText: (r) => r.requestedTimestamp?.toString() ?? "",
        renderFunc: (_val, data) =>
          data.requestedTimestamp != null ? (
            <Flex vertical>
              <FormattedDate timestamp={data.requestedTimestamp} />
              <Flex align="center" gap={4}>
                <NotifierConversationLinks
                  notifications={data.notifications}
                  size={spacing.scale.md}
                />
                <FormattedTime timestamp={data.requestedTimestamp} />
              </Flex>
            </Flex>
          ) : undefined,
        width: 150,
      },
      {
        columnName: "requestor",
        sortable: true,
        filterable: true,
        // Filter and sort on the raw requestor (so e.g. the "requested by me"
        // quick filter matches agentic requests); the cell renders the
        // human-readable label, and search also matches the full attribution
        // (e.g. agent subjects)
        toText: (r) => r.requestor,
        toSearch: (r) => requestorAttribution(r).map((r) => r.sub),
        renderFunc: (_val, data) => (
          <RequestorCell
            clientId={data.agentic?.agents?.[0]?.sub}
            attribution={toRequestAttribution(data)}
          />
        ),
        width: 200,
      },
      {
        columnName: "resource",
        sortable: true,
        filterable: true,
        toText: (r) => toRequestDescription(r),
        toSearch: (r) =>
          compact([
            toRequestLocation(r, flags),
            toRequestType(r),
            toRequestAccessType(r),
          ]),
        renderFunc: (_val, request) => (
          <RequestResource request={request} flags={flags} />
        ),
        width: 300,
      },
      {
        columnName: "approver",
        sortable: true,
        filterable: true,
        toText: (r) => {
          if (!r.approvalDetails) return "";
          const { email, id, name, approvalSource } = r.approvalDetails;
          const displayText =
            approvalSource === "evidence"
              ? "Pre Approval"
              : email || name || id;
          return displayText === "persistent"
            ? "By persistent access"
            : displayText;
        },
        renderFunc: (_val, data, displayName) =>
          data.approvalDetails && (
            <ApproverCell
              approvalSource={data.approvalDetails.approvalSource}
              username={displayName}
              timestamp={data.approvalDetails?.approvedTimestamp}
              preApproval={getPreApprovalData(data)}
            />
          ),
        width: 200,
      },
      {
        columnName: "reason",
        sortable: false,
        filterable: false,
        toText: (r) => r.reason,
        renderFunc: (_val, request) => {
          return <RequestReason request={request} />;
        },

        width: 300,
      },
    ],
    [flags]
  );

  const requestColumnNames = requestColumns.map((item) => item.columnName);

  const useTableFilters = (): [
    TableFilters,
    (filters: TableFilters) => void,
  ] => {
    const [searchParams, setSearchParams] = useSearchParams();

    const tableFilters: TableFilters = useMemo(() => {
      const deserializedFiltersArray = requestColumnNames.map((columnName) => ({
        columnName,
        value: searchParams
          .getAll(columnName)
          .map((value) => deserializeQueryParamValue(value)),
      }));
      return deserializedFiltersArray.reduce(
        (prev, curr) => ({ ...prev, [curr.columnName]: curr.value }),
        {}
      );
    }, [searchParams]);

    const innerSetFilters = useCallback(
      (filters: TableFilters) => {
        // transform new filters into array format
        const newFiltersArray = requestColumnNames.map((columnName) => ({
          columnName,
          value: filters[columnName] || null,
        }));

        // serialize filter values
        const serializedFilters: Partial<
          Record<RequestColumnName, string[] | null>
        > = newFiltersArray.reduce((prev, curr) => {
          return {
            ...prev,
            [curr.columnName]:
              curr.value?.map((item) =>
                serializeQueryParamValue(
                  typeof item === "bigint" ? item.toString() : item
                )
              ) || null,
          };
        }, {});

        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          for (const [columnName, columnFilterValues] of Object.entries(
            serializedFilters
          )) {
            next.delete(columnName);
            if (
              columnFilterValues === null ||
              columnFilterValues === undefined ||
              columnFilterValues.length === 0
            ) {
              continue;
            }

            // set filters
            for (const v of columnFilterValues) {
              if (!!v) next.append(columnName, v);
            }
          }

          return next;
        });
      },
      [setSearchParams]
    );

    return [tableFilters, innerSetFilters];
  };

  const [tableFilters, setTableFilters] = useTableFilters();

  const [pagination, setPagination] = usePagination();

  const updateSearchQuery = useCallback(
    (query: typeof searchQuery) => {
      setSearchQuery(query ?? null);
    },
    [setSearchQuery]
  );

  const updatePagination = useCallback(
    (page: number, pageSize: number) => setPagination({ page, pageSize }),
    [setPagination]
  );

  const onTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>
    ) => {
      setTableFilters(filters);
    },
    [setTableFilters]
  );

  const pendingQuickFilterAction = useCallback(() => {
    quickFilterAction(
      "status",
      "Pending Approval",
      tableFilters,
      setTableFilters
    );
  }, [tableFilters, setTableFilters]);

  const requestedByMeQuickFilterAction = useCallback(() => {
    if (userEmail) {
      quickFilterAction("requestor", userEmail, tableFilters, setTableFilters);
    }
  }, [tableFilters, setTableFilters, userEmail]);

  const activeGrantsQuickFilterAction = useCallback(() => {
    quickFilterAction("status", "Granted", tableFilters, setTableFilters);
  }, [tableFilters, setTableFilters]);

  const clearQuickFilterAction = useCallback(() => {
    /*
     * Note: it's not save to call the other functions individually as one might expect like this:
     *
     * updateSearchQuery("");
     * updateApprovedDateFilter(null);
     * updateRequestedDateFilter(null);
     *
     * It is better to batch related calls to setSearchParams, and the reason is related to the
     * fact that it directly call's the browser's navigation API under the hood. Not batching
     * the calls can sometimes lead to unexpected behavior.
     *
     * See https://github.com/remix-run/react-router/issues/12179
     * and https://github.com/remix-run/react-router/issues/11752
     *
     */
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const columns = useMemo((): ResizableTableColumnType<RequestRow>[] => {
    const buildColumn = ({
      columnName: dataIndex,
      filterable,
      sortable,
      toText,
      renderFunc,
      width,
    }: RequestColumn): ResizableTableColumnType<RequestRow> => ({
      dataIndex,
      width,
      title: startCase(toLower(dataIndex)),
      render: (val, data) => renderFunc(val, data, toText(data) ?? ""),
      ...(filterable
        ? {
            filteredValue: tableFilters?.[dataIndex] ?? [],
            filterSearch: true,
            filters: (() => {
              return compact(uniq(data.map(toText))).map((value) => ({
                text: value,
                value,
              }));
            })(),
            onFilter: (value, record) => toText(record) === value,
          }
        : {}),
      ...(sortable
        ? {
            sorter:
              dataIndex === "requested"
                ? (a, b) => {
                    const at = a.requestedTimestamp;
                    const bt = b.requestedTimestamp;
                    if (at == null && bt == null) return 0;
                    if (at == null) return 1;
                    if (bt == null) return -1;
                    return at - bt;
                  }
                : (a, b) => (toText(a) ?? "").localeCompare(toText(b) ?? ""),
          }
        : {}),
    });

    return requestColumns.map((c) => buildColumn(c));
  }, [data, tableFilters, requestColumns]);

  const deleteFilter = useCallback(
    (selectedFilter: React.Key, columnName: ColumnTitle<PermissionRequest>) => {
      const fieldDataIndex = requestColumns.find(
        (c) => startCase(c.columnName) === columnName
      )?.columnName;
      if (fieldDataIndex) {
        const newFilters = {
          ...tableFilters,
          [fieldDataIndex]: tableFilters[fieldDataIndex]?.filter(
            (v) => v !== selectedFilter
          ),
        };
        setTableFilters(newFilters);
      }
    },
    [tableFilters, setTableFilters, requestColumns]
  );

  const filterData = useMemo(() => {
    const requestMatchesAnyDisplayText = (
      request: PermissionRequest,
      query: string | null
    ) => {
      if (!query) return true;
      const normalizedQuery = query.toLowerCase();
      for (const col of requestColumns) {
        const text = col.toText(request);
        if (
          (typeof text === "string" &&
            text.toLowerCase().includes(normalizedQuery)) ||
          col
            .toSearch?.(request)
            ?.some(
              (d) =>
                typeof d === "string" &&
                d.toLowerCase().includes(normalizedQuery)
            )
        ) {
          return true;
        }
      }
    };

    return throttle(
      () =>
        data.filter((item: PermissionRequest) => {
          const { approvalDetails, requestedTimestamp } = item;

          const [approvedAfter, approvedBefore] = approvedFilter;
          const [requestedAfter, requestedBefore] = requestedFilter;

          const isApprovedAfter =
            !approvedAfter ||
            gt(approvalDetails?.approvedTimestamp, approvedAfter.getTime());
          const isApprovedBefore =
            !approvedBefore ||
            lt(approvalDetails?.approvedTimestamp, approvedBefore.getTime());
          const isRequestedAfter =
            !requestedAfter || gt(requestedTimestamp, requestedAfter.getTime());
          const isRequestedBefore =
            !requestedBefore ||
            lt(requestedTimestamp, requestedBefore.getTime());

          return (
            requestMatchesAnyDisplayText(item, searchQuery) &&
            isApprovedAfter &&
            isApprovedBefore &&
            isRequestedAfter &&
            isRequestedBefore
          );
        }),
      300
    )();
  }, [approvedFilter, data, requestColumns, requestedFilter, searchQuery]);

  return (
    <Space direction="vertical" size="middle">
      <div className="w-3/4">
        <SearchBar
          onChange={updateSearchQuery}
          requests={requests}
          searchQuery={searchQuery}
        />
      </div>
      <div className="w-3/4">
        <RequestDateFilters
          approvedDateFilter={approvedFilter}
          requestedDateFilter={requestedFilter}
          onApprovalFilterChange={setApprovedFilter}
          onRequestFilterChange={setRequestedFilter}
        />
      </div>
      <Space
        direction={md ? "horizontal" : "vertical"}
        size="middle"
        /* eslint-disable-line react/forbid-component-props */ style={{
          width: "75%",
          minHeight: md ? "2em" : "unset",
        }}
      >
        <Prefix className="w-[40em]" prefix="Quick Filters">
          <Space size="middle">
            <Button
              type="link"
              size="small"
              onClick={activeGrantsQuickFilterAction}
            >
              Active Grants
            </Button>
            <Button type="link" size="small" onClick={pendingQuickFilterAction}>
              Pending Approval
            </Button>
            {userEmail && (
              <Button
                type="link"
                size="small"
                onClick={requestedByMeQuickFilterAction}
              >
                Requested by Me
              </Button>
            )}
            <Button type="link" size="small" onClick={clearQuickFilterAction}>
              Clear
            </Button>
          </Space>
        </Prefix>
        <Space size={1} wrap>
          {tableFilters &&
            Object.entries(tableFilters).map(([key, value]) => {
              return value?.map((v) => (
                <FilterTag
                  key={`${key}-${v}`}
                  text={v.toLocaleString()}
                  fieldName={startCase(
                    requestColumns.find((c) => c.columnName === key)?.columnName
                  )}
                  handleDelete={deleteFilter}
                />
              ));
            })}
        </Space>
      </Space>
      {loading ? (
        <Spin />
      ) : hasRequests ? (
        <>
          {/* `wrap-normal` on headers and cell typography keeps words from
              breaking mid-word; rows are clickable and show a focus ring. */}
          <ResizableTable<RequestRow>
            className="[&_.ant-table-thead>tr>th]:[text-wrap:balance]! [&_.ant-table-thead>tr>th]:wrap-normal! [&_.ant-table-tbody>tr]:cursor-pointer! [&_.ant-table-tbody>tr:focus-visible]:outline-2! [&_.ant-table-tbody>tr:focus-visible]:-outline-offset-2! [&_.ant-table-tbody>tr:focus-visible]:outline-[var(--ant-color-primary,#1677ff)]! [&_.ant-table-tbody>tr>td]:border-b-0! [&_td_.ant-typography]:[word-break:normal]!"
            tableId={ResizableTableKeys.frequentRequests}
            onChange={onTableChange}
            size="small"
            tableLayout="fixed"
            columns={columns}
            dataSource={filterData}
            rowKey="id"
            onRow={handleRowClick}
            pagination={{
              current: pagination.page,
              defaultCurrent: 1,
              pageSize: pagination.pageSize,
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: updatePagination,
            }}
          />
          {requestId && <RequestDetailDrawer requestId={requestId} />}
        </>
      ) : (
        <Empty description="You don't have any requests yet." />
      )}
    </Space>
  ); // Each row must have a unique ID; silence the runtime warning by adding the rowKey attribute
};

const RequestStatus: React.FC<{ request: PermissionRequest }> = ({
  request,
}) => {
  return (
    <Flex vertical gap={4}>
      <RequestStatusTag request={request} />
      <Typography.Text type="secondary" className="sub-text">
        {request.isAwaitingExpiry && request.expiryTimestamp
          ? `Expires ${formatDistanceStrict(
              request.expiryTimestamp,
              Date.now(),
              {
                addSuffix: true,
              }
            )}`
          : request.lastUpdatedTimestamp
            ? `${formatDistanceStrict(
                request.lastUpdatedTimestamp,
                Date.now(),
                {
                  addSuffix: true,
                }
              )}`
            : null}
      </Typography.Text>
    </Flex>
  );
};

const RequestResource: React.FC<{
  request: PermissionRequest;
  flags: Record<string, boolean>;
}> = ({ request, flags }) => {
  const displayName = toRequestDescription(request);
  const accessType = toRequestAccessType(request);
  const location = toRequestLocation(request, flags);

  return (
    isKeyOf(AllIntegrationLogos, request.type) && (
      <Flex align="center" gap={12}>
        <div className="shrink-0">{AllIntegrationLogos[request.type]}</div>
        <Flex vertical className="min-w-0">
          <Typography.Text ellipsis>{displayName}</Typography.Text>
          <Typography.Text type="secondary" className="sub-text" ellipsis>
            {location} • {accessType}
          </Typography.Text>
        </Flex>
      </Flex>
    )
  );
};

const RequestReason: React.FC<{ request: PermissionRequest }> = ({
  request,
}) => {
  const reason = useMemo(() => request.reason?.trim(), [request.reason]);

  return (
    <Flex>
      <Typography.Text
        ellipsis
        className="sub-text"
        /* eslint-disable-line react/forbid-component-props */ style={{
          color: reason ? "black" : undefined,
          fontStyle: reason ? "normal" : "italic",
        }}
      >
        {reason ?? "No reason provided"}
      </Typography.Text>
    </Flex>
  );
};
